import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaRobot, FaBrain, FaCogs, FaNetworkWired, FaMicrochip, FaDatabase,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaLightbulb, FaRocket,
    FaChartLine, FaFingerprint
} from 'react-icons/fa';
import heroImage from '../../assets/background in the concept of technology_ _ Premium Vector.png';

const AISolutionsPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaBrain, title: 'Machine Learning', desc: 'Predictive models that learn from your data to foresee trends.', image: 'https://images.unsplash.com/photo-1555255707-c07966088b7b?w=600' },
        { icon: FaRobot, title: 'Natural Language Processing', desc: 'Chatbots and analysis tools that understand human language.', image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=600' },
        { icon: FaCogs, title: 'Process Automation', desc: 'RPA bots that handle repetitive tasks with zero errors.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600' },
        { icon: FaDatabase, title: 'Big Data Solutions', desc: 'Architectures to handle and process massive datasets in real-time.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaFingerprint, title: 'Computer Vision', desc: 'Image recognition systems for security, QC, and analysis.', image: 'https://images.unsplash.com/photo-1561736778-92e52a7769ef?w=600' },
        { icon: FaNetworkWired, title: 'AI Integration', desc: 'Embedding AI capabilities into your existing software ecosystem.', image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600' }
    ];

    const techStack = [
        { name: 'TensorFlow', icon: FaBrain },
        { name: 'PyTorch', icon: FaMicrochip },
        { name: 'Keras', icon: FaNetworkWired },
        { name: 'OpenCV', icon: FaFingerprint },
        { name: 'Scikit-learn', icon: FaChartLine },
        { name: 'Dialogflow', icon: FaRobot }
    ];

    // Horizontal Flow Steps (AI Implementation)
    const flowSteps = [
        { title: 'Discovery', icon: FaLightbulb, color: 'from-cyan-400 to-blue-500' },
        { title: 'Data Prep', icon: FaDatabase, color: 'from-blue-500 to-indigo-600' },
        { title: 'Modeling', icon: FaBrain, color: 'from-purple-500 to-pink-500' },
        { title: 'Training', icon: FaMicrochip, color: 'from-red-500 to-orange-500' },
        { title: 'Integration', icon: FaCogs, color: 'from-orange-500 to-yellow-500' },
        { title: 'Deployment', icon: FaRocket, color: 'from-green-500 to-emerald-500' }
    ];

    const faqs = [
        { q: 'How can AI help my business?', a: 'AI automates routine tasks, provides deep insights from data, enhances customer service via chatbots, and predicts future trends to help you make better decisions.' },
        { q: 'Is AI expensive to implement?', a: 'It varies. We offer scalable solutions, from simple automation scripts to complex enterprise ML models. We start with a high-ROI pilot project.' },
        { q: 'Do I need a lot of data?', a: 'Ideally, yes. But we can start with what you have, or use pre-trained models and transfer learning to get results with smaller datasets.' },
        { q: 'Is my data secure?', a: 'Absolutely. We follow strict data governance protocols and can deploy models on-premise or in your private cloud to ensure data never leaves your control.' },
        { q: 'How long does an AI project take?', a: 'A Proof of Concept (PoC) can take 4-6 weeks. Full deployment depends on complexity, typically 3-6 months.' }
    ];

    const whyChooseUs = [
        { title: 'PhD-Level Expertise', desc: 'Our team includes data scientists with deep academic and practical experience.' },
        { title: 'Ethical AI', desc: 'We design transparent, fair, and unbiased AI systems.' },
        { title: 'End-to-End Service', desc: 'From data cleaning to model deployment and maintenance.' },
        { title: 'Technology Agnostic', desc: 'We use the best tools for the job, whether it\'s TensorFlow, PyTorch, or OpenAI.' },
        { title: 'Business Focus', desc: 'We don\'t just build models; we solve business problems.' },
        { title: 'Ongoing Support', desc: 'AI needs retraining. We stick around to ensure your model stays accurate.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-800/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-cyan-400 mb-4">Innovation &gt; Intelligence</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Artificial Intelligence <br /><span className="text-cyan-400 font-normal">Solutions</span></h1>
                        <p className="text-lg font-light text-gray-300 leading-relaxed mb-8 max-w-2xl">
                            Transform your business with intelligent algorithms. From predictive analytics to autonomous systems, we build the future.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-cyan-500 text-white font-medium rounded-sm hover:bg-cyan-600 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Consult An Expert
                            </a>
                            <a href="#services" className="px-8 py-3 border border-gray-400 text-white font-medium rounded-sm hover:bg-white hover:text-slate-900 transition-all text-sm uppercase tracking-wide">
                                Explore Capabilities
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">The AI Advantage</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Leverage the power of data to outpace the competition.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Models Deployed', val: '50+' },
                            { label: 'Data Processed', val: '10TB+' },
                            { label: 'Accuracy', val: '99.8%' },
                            { label: 'Efficiency Gain', val: '40%' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-cyan-500 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16">
                        <div className="max-w-2xl">
                            <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Our Core Capabilities</h2>
                            <p className="text-lg text-slate-600">
                                Comprehensive AI services tailored to your industry.
                            </p>
                        </div>
                        <a href="/contact" className="hidden md:inline-block text-cyan-600 font-medium hover:text-cyan-700 hover:underline mt-4 md:mt-0">View All Services &rarr;</a>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center opacity-60 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-40" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-90"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-cyan-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-gray-300 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-cyan-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Powered By Best-in-Class Technology</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {techStack.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-cyan-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">From Concept to Code</h2>
                        <p className="text-slate-600">Our systematic approach to building intelligence.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-cyan-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Intelligence <br />Without Compromise</h2>
                            <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                We combine academic rigor with engineering excellence. Our solutions aren't just theoretically sound—they're built to perform in the real world.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-cyan-400 font-medium hover:text-white transition-colors">
                                Start Your Transformation <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-slate-800/50 p-6 rounded-sm border border-slate-700 hover:border-cyan-500/50 transition-colors">
                                    <FaCheckCircle className="text-cyan-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Common Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-cyan-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-cyan-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Build The Future</h2>
                            <p className="text-slate-500 mb-8">Tell us about your challenge. We'll engineer the solution.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="Jane Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="Acme Inc." />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-cyan-500 outline-none transition-colors" placeholder="jane@acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Interest</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-cyan-500 outline-none transition-colors text-slate-700">
                                        <option>AI Consulting</option>
                                        <option>Machine Learning Model</option>
                                        <option>Chatbot / NLP</option>
                                        <option>Process Automation</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-cyan-500 text-white font-bold py-4 rounded-sm hover:bg-cyan-600 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Request Consultation
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AISolutionsPage;
